import { useCallback } from 'react';
import { useToastStore } from '../stores/toast.store';

export interface UndoToastOptions {
  message: string;
  undoAction: () => void | Promise<void>;
  /** Called when the undo window expires without the user clicking Undo. */
  onTimeout?: () => void;
  /** Duration in ms before the toast auto-dismisses. Default 5000. */
  durationMs?: number;
}

/**
 * Encapsulates the optimistic-then-undo pattern (Layer 6 D47).
 *
 * Usage:
 *   const undoToast = useUndoToast();
 *   // caller performs the action first, then calls undoToast:
 *   await archiveAgent(id, true);
 *   undoToast({
 *     message: 'Agent archived',
 *     undoAction: () => archiveAgent(id, false),
 *     onTimeout: () => {},
 *     durationMs: 5000,
 *   });
 *
 * Internally uses useToastStore.push with action.label='Undo'.
 */
export function useUndoToast() {
  const pushToast = useToastStore((s) => s.push);

  return useCallback(
    (opts: UndoToastOptions) => {
      const duration = opts.durationMs ?? 5_000;
      let undone = false;

      pushToast({
        message: opts.message,
        durationMs: duration,
        action: {
          label: 'Undo',
          onClick: () => {
            undone = true;
            void opts.undoAction();
          },
        },
      });

      if (opts.onTimeout) {
        const cb = opts.onTimeout;
        setTimeout(() => {
          if (!undone) cb();
        }, duration);
      }
    },
    [pushToast],
  );
}
